import {
  Coupon,
  Coupon_DiscountValue,
  Coupon_Tier,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';
import {GetHeaderResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/coupons/v1/landingpage/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  BrandLogo,
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  GenericIconName,
  Icon,
  ModalDialog,
  RowSection,
  SPLIT_VIEW_SECTION_PADDING,
  SPLIT_VIEW_SECTION_PADDING_MIN_L,
  Text,
  UnstyledButton,
  Z_INDEXES,
  desktopAndUp,
  mediaDesktopAndUp,
  mediaLargeScreens,
  mediaMaxS,
  mediaMinL,
  tabletAndDown,
  useInView,
} from '@aboutyou/ui-components';
import {styled as linariaStyled} from '@linaria/react';
import React, {useEffect, useRef} from 'react';
import {useRect} from 'react-viewport-utils';
import sc, {css} from 'styled-components';
import {CouponTiers} from '../../../features/CouponCard/components/CouponTiers';
import {
  AppOnlyIcon,
  CouponAppOnlyBadge,
} from '../../../features/Coupons/CouponBadges/CouponBadges';
import {
  CouponExpiryDateCountdown,
  StyledCouponExpiryDateCountdownIcon,
  StyledCouponExpiryDateCountdownUnitComponent,
} from '../../../features/Coupons/CouponExpiryDateCountdown/CouponExpiryDateCountdown';
import {CouponInfoContentPopper} from '../../../features/Coupons/CouponInfoContentPopper/CouponInfoContentPopper';
import {useCouponInfoContentPopperStateManager} from '../../../features/Coupons/CouponInfoContentPopper/useCouponInfoContentPopperStateManager';
import {isAppOnlyCoupon as getIsAppOnlyCoupon} from '../../../features/Coupons/isAppOnlyCoupon';
import {getCouponValues} from '../../../features/Coupons/utils/getCouponValues';
import {useIsUrgencyTriggersDisabled} from '../../../shared-deprecated/useIsUrgencyTriggersDisabled';
import headerBackgroundExpanded from './assets/header-background-expanded.svg';
import {useCLPRedirect} from './hooks/useCLPRedirect';

const CLP_INFO_ICON_CUSTOM_KEY = 'CLP_INFO_ICON_CUSTOM_KEY';

const ExpiryBadges: React.FC<{
  expiryDate: Date | undefined;
  isAppOnlyCoupon: boolean;
  collapsedBadgeWrapperRef: React.RefObject<HTMLDivElement | null>;
  countDownBadgeRef: React.RefObject<HTMLDivElement | null>;
  appOnlyBadgeLeftAdjustment: number;
}> = ({
  expiryDate,
  isAppOnlyCoupon,
  collapsedBadgeWrapperRef,
  countDownBadgeRef,
  appOnlyBadgeLeftAdjustment,
}) => {
  if (!expiryDate) return null;

  return (
    <>
      <BadgesWrapperExpanded>
        <StyledCouponExpiryDateCountdown
          expiryDate={expiryDate}
          preventUnitComponentEqualWidth
        />
        {isAppOnlyCoupon && <StyledCouponAppOnlyBadge />}
      </BadgesWrapperExpanded>
      <BadgesWrapperCollapsed
        ref={collapsedBadgeWrapperRef}
        $countDownBadgeWidth={appOnlyBadgeLeftAdjustment}
      >
        <StyledCouponExpiryDateCountdown
          expiryDate={expiryDate}
          preventUnitComponentEqualWidth
          ref={countDownBadgeRef}
        />
        {isAppOnlyCoupon && <StyledCouponAppOnlyBadge />}
      </BadgesWrapperCollapsed>
    </>
  );
};

// Component for discount section
const DiscountSection: React.FC<{
  discount: Coupon_DiscountValue | undefined;
  discountCondition: string | undefined;
}> = ({discount, discountCondition}) => {
  if (!discount) return null;

  return (
    <Discount>
      <DiscountValue size="px56" weight="heavy" color="PrimaryCoupon">
        {discount.value}
        {discount.unit}
      </DiscountValue>
      {discountCondition && (
        <DiscountCondition size="px16" color="PrimaryCoupon">
          {discountCondition}
        </DiscountCondition>
      )}
    </Discount>
  );
};

// Component for headline section
const HeadlineSection: React.FC<{
  categoryName: string | undefined;
  headline: string | undefined;
}> = ({categoryName, headline}) => {
  const shouldShowHeadline = categoryName || headline;

  if (!shouldShowHeadline) return null;

  return (
    <HeadlineContainer>
      {categoryName && <Category>{categoryName}</Category>}
      {headline && (
        <Headline $preventVerticalAdjustment={!categoryName}>
          {headline}
        </Headline>
      )}
    </HeadlineContainer>
  );
};

const MainContentSection: React.FC<{
  couponTiersData: Coupon_Tier[] | undefined;
  hasBrandLogo: boolean;
  discount: Coupon_DiscountValue | undefined;
  discountCondition: string | undefined;
  categoryName: string | undefined;
  headline: string | undefined;
}> = ({
  couponTiersData,
  hasBrandLogo,
  discount,
  discountCondition,
  categoryName,
  headline,
}) => {
  const hasMultipleTiers = couponTiersData && couponTiersData.length > 1;

  if (hasMultipleTiers) {
    return (
      <StyledCouponTiers
        $hasBrandLogo={hasBrandLogo}
        mode="compact"
        forceActiveState
        tiers={couponTiersData}
      />
    );
  }

  return (
    <>
      <DiscountSection
        discount={discount}
        discountCondition={discountCondition}
      />
      <HeadlineSection categoryName={categoryName} headline={headline} />
    </>
  );
};

export interface CouponLandingPageHeaderViewDesktopProps {
  className?: string;
  setCoupon: React.Dispatch<React.SetStateAction<Coupon | undefined>>;
}

export const CouponLandingPageHeaderViewDesktop: React.FC<
  GetHeaderResponse & CouponLandingPageHeaderViewDesktopProps
> = ({coupon, className, setCoupon, redirect}) => {
  const {t} = useTranslation();
  const {open: openInfoDialog} = useCouponInfoContentPopperStateManager(
    CLP_INFO_ICON_CUSTOM_KEY,
  );
  const disableUrgencyTriggers = useIsUrgencyTriggersDisabled();

  const {
    badgesWrapperWidth,
    collapsedBadgeWrapperRef,
    countDownBadgeRef,
    countDownBadgeWidth,
  } = useGetComponentRects(coupon);

  useCLPRedirect(redirect);

  const {ref, inView} = useInView<HTMLDivElement>({
    threshold: 0,
  });

  useEffect(() => {
    if (coupon) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setCoupon(coupon);
    }
  }, [coupon]);

  if (!coupon) {
    return null;
  }

  const {
    brand,
    categoryName,
    discount,
    discountCondition,
    expiryDate,
    headline,
    info,
  } = getCouponValues(coupon);

  const hasBrandLogo = Boolean(brand?.image);
  const isTwoTierMOVCoupon =
    coupon.type?.$case === 'minimumOrderValueCoupon' &&
    coupon.type?.minimumOrderValueCoupon.tiers.length === 2;
  const isAppOnlyCoupon = getIsAppOnlyCoupon(coupon);
  const appOnlyBadgeLeftAdjustment = isAppOnlyCoupon ? countDownBadgeWidth : 0;

  const couponTiersData = getCouponTiersData(coupon);
  const hasMultipleTiers = couponTiersData && couponTiersData.length > 1;

  return (
    <>
      <div ref={ref} />

      <Container className={className} data-collapsed={!inView}>
        <Header data-testid="couponLandingPageHeader">
          <InfoButton
            data-testid="couponLandingPageHeaderInfoIcon"
            onClick={() => openInfoDialog()}
            aria-label={t('Open info window')}
          >
            <InfoIcon
              asset={GenericIconName.InfoWithoutFill}
              fill={COLORS.PrimaryCoupon}
            />
          </InfoButton>
          <CouponInfoContentPopper
            LayoutComponent={ModalDialog}
            couponInfo={info}
            stateManagerCustomKey={CLP_INFO_ICON_CUSTOM_KEY}
          />
          <HeaderContent $centerItems={Boolean(hasMultipleTiers)}>
            {!disableUrgencyTriggers && (
              <ExpiryBadges
                expiryDate={expiryDate}
                isAppOnlyCoupon={isAppOnlyCoupon}
                collapsedBadgeWrapperRef={collapsedBadgeWrapperRef}
                countDownBadgeRef={countDownBadgeRef}
                appOnlyBadgeLeftAdjustment={appOnlyBadgeLeftAdjustment}
              />
            )}
            <ContentAndBrandLogo>
              <DiscountAndHeadlineWrapper
                $translateX={badgesWrapperWidth}
                $hasTearsShown={Boolean(hasMultipleTiers)}
                $isTwoTierMOVCoupon={isTwoTierMOVCoupon}
              >
                <MainContentSection
                  couponTiersData={couponTiersData}
                  hasBrandLogo={hasBrandLogo}
                  discount={discount}
                  discountCondition={discountCondition}
                  categoryName={categoryName}
                  headline={headline}
                />
              </DiscountAndHeadlineWrapper>
              {hasBrandLogo && brand?.image && (
                <StyledBrandLogo image={brand.image} name={brand.name || ''} />
              )}
            </ContentAndBrandLogo>
          </HeaderContent>
        </Header>
      </Container>
    </>
  );
};

const getCouponTiersData = (coupon: Coupon) => {
  if (coupon.type?.$case === 'minimumOrderValueCoupon') {
    return coupon.type.minimumOrderValueCoupon.tiers;
  }

  if (coupon.type?.$case === 'highTierCoupon') {
    return coupon.type.highTierCoupon.discount?.$case === 'tiers'
      ? coupon.type.highTierCoupon.discount.tiers.tiers
      : undefined;
  }

  return undefined;
};

const useGetComponentRects = (coupon?: Coupon) => {
  const collapsedBadgeWrapperRef = useRef<HTMLDivElement>(null);
  const countDownBadgeRef = useRef<HTMLDivElement>(null);

  const {width: badgesWrapperWidth} = useRect(
    collapsedBadgeWrapperRef,
    {
      disableScrollUpdates: true,
      disableDimensionsUpdates: true,
      priority: 'low',
    },
    [coupon],
  ) || {width: 0};

  const {width: countDownBadgeWidth} = useRect(
    countDownBadgeRef,
    {
      disableScrollUpdates: true,
      disableDimensionsUpdates: true,
      priority: 'low',
    },
    [coupon],
  ) || {width: 0};

  return {
    collapsedBadgeWrapperRef,
    countDownBadgeRef,
    badgesWrapperWidth,
    countDownBadgeWidth,
  };
};

const DiscountAndHeadlineWrapper = sc.div<{
  $translateX: number;
  $hasTearsShown?: boolean;
  $isTwoTierMOVCoupon?: boolean;
  $appOnlyBadgeLeftAdjustment?: number;
}>`
  --padding-adjustment: 28px;

  display: flex;
  align-items: center;
  flex: 1;
  transition: transform var(--animation-duration),
    padding-right var(--animation-duration);
  transform-origin: center left;

  ${({$hasTearsShown, $isTwoTierMOVCoupon}) =>
    $hasTearsShown &&
    css`
      margin: 0 auto;
      --padding-adjustment: ${$isTwoTierMOVCoupon ? '30px' : '0px'};
    `}

  [data-collapsed='true'] & {
    padding-right: 0;
    margin: 0;

    ${({$translateX, $appOnlyBadgeLeftAdjustment}) => css`
      padding-left: calc(
        ${$translateX}px - ${$appOnlyBadgeLeftAdjustment ?? 0}px -
          var(--padding-adjustment)
      );
    `}

    ${({$translateX}) =>
      desktopAndUp(css`
        padding-left: calc(${$translateX}px - var(--padding-adjustment));
      `)}
  }
`;

const Container = linariaStyled(RowSection)`
  --animation-duration: 300ms;

  position: sticky;
  top: 28px;
  display: flex;
  justify-content: flex-end;
  width: 100%;
  height: 180px;
  z-index: ${Z_INDEXES.COUPON_LANDING_PAGE_HEADER};
  transition: transform var(--animation-duration);
  pointer-events: none;

  &[data-collapsed='true'] {
    transform: translateY(-28px);
  }
`;

const Header = sc.div`
  --height-expanded: 180px;
  --height-collapsed: 64px;
  --width: 70%;
  --padding: ${SPLIT_VIEW_SECTION_PADDING};

  width: calc(var(--width) - var(--padding));
  height: var(--height-expanded);
  color: ${COLORS.PrimaryCoupon};

  // eslint-disable-next-line local-rules/css-layout-animation
  transition: height var(--animation-duration);

  pointer-events: auto;

  [data-collapsed='true'] & {
    height: var(--height-collapsed);
  }

  @media ${mediaDesktopAndUp} {
    --width: 80%;
  }

  @media ${mediaLargeScreens} {
    --width: 85%;
  }

  @media ${mediaMinL} {
    --padding: ${SPLIT_VIEW_SECTION_PADDING_MIN_L};
  }

  &::before {
    --width: 0.7;
    --margin: 20px;
    --padding: ${SPLIT_VIEW_SECTION_PADDING};

    content: '';
    position: absolute;
    inset: 0;
    margin-left: auto;
    margin-right: var(--margin);
    width: calc(
      (100% - var(--margin) - var(--margin)) * var(--width) - var(--padding)
    );
    height: var(--height-expanded);
    border-radius: 8px;
    background: url(${headerBackgroundExpanded}) no-repeat center/cover;
    z-index: -1;

    // eslint-disable-next-line local-rules/css-layout-animation
    transition: width var(--animation-duration),
      height var(--animation-duration), border-radius var(--animation-duration),
      margin var(--animation-duration);
    transform-origin: top;

    [data-collapsed='true'] & {
      width: 100%;
      height: var(--height-collapsed);
      border-radius: 0;
      margin-right: 0;
    }

    @media ${mediaDesktopAndUp} {
      --width: 0.8;
      --margin: 30px;
    }

    @media ${mediaLargeScreens} {
      --width: 0.85;
      --margin: 60px;
    }

    @media ${mediaMinL} {
      --width: 0.85;
      --margin: 60px;
      --padding: ${SPLIT_VIEW_SECTION_PADDING_MIN_L};
    }
  }
`;

const InfoButton = sc(UnstyledButton)`
  --padding: 20px;

  position: absolute;
  top: 8px;
  right: 0;
  transform: translateX(calc(-12px - var(--padding)));
  z-index: 1;
  transition: transform var(--animation-duration);
  cursor: pointer;

  [data-collapsed='true'] & {
    --padding: 0px;
  }

  @media ${mediaDesktopAndUp} {
    --padding: 30px;
  }

  @media ${mediaLargeScreens} {
    --padding: 60px;
  }
`;

const InfoIcon = sc(Icon)`
  width: 18px;
  height: 18px;
`;

const HeaderContent = sc.div<{$centerItems: boolean}>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 20px 48px;

  ${({$centerItems}) =>
    $centerItems &&
    css`
      padding-right: 48px;
      justify-content: center;
    `}
`;

const BadgesSharedStyles = css`
  font-size: ${FONT_SIZES.px10};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: 1.42857;
  padding: 2px 10px;

  ${desktopAndUp(css`
    font-size: ${FONT_SIZES.px14};
    padding: 4px 16px;
  `)}
`;

const StyledCouponExpiryDateCountdown = sc(CouponExpiryDateCountdown)`
  --icon-size: 18px;
  padding: 2px 10px;
  width: fit-content;

  ${desktopAndUp(css`
    width: auto;
    padding: 4px 16px;
    --icon-size: 24px;
  `)}

  ${StyledCouponExpiryDateCountdownIcon}${StyledCouponExpiryDateCountdownIcon} {
    width: var(--icon-size);
    height: var(--icon-size);
    flex-basis: var(--icon-size);
  }

  ${StyledCouponExpiryDateCountdownUnitComponent} {
    padding: 0;
    font-size: ${FONT_SIZES.px10};

    ${desktopAndUp(css`
      font-size: ${FONT_SIZES.px14};
      padding: 0;
    `)}
  }
`;

const StyledCouponAppOnlyBadge = sc(CouponAppOnlyBadge).attrs(() => ({
  iconSize: 'S',
}))`
  ${BadgesSharedStyles}
  margin-left: 0;
  --icon-size: 18px;
  justify-content: center;

  ${desktopAndUp(css`
    --icon-size: 24px;
  `)}

  ${AppOnlyIcon}${AppOnlyIcon} {
    width: var(--icon-size);
    height: var(--icon-size);
    flex-basis: var(--icon-size);
  }

  margin: 0;
`;

const BadgesWrapperExpanded = sc.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  transition: opacity var(--animation-duration);
  display: flex;
  gap: 4px;

  ${desktopAndUp(css`
    gap: 8px;
  `)}

  [data-collapsed='true'] & {
    opacity: 0;
  }
`;

const BadgesWrapperCollapsed = sc.div<{$countDownBadgeWidth: number}>`
  position: absolute;
  left: 0;
  opacity: 0;
  transform: translateX(130px);
  transition: opacity var(--animation-duration),
    transform var(--animation-duration);
  display: flex;
  gap: 4px;
  margin-left: -${props => props.$countDownBadgeWidth}px;

  ${desktopAndUp(css`
    margin-left: 0;
    gap: 8px;
  `)}

  [data-collapsed='true'] & {
    transform: translateX(0);
    opacity: 1;
  }
`;

const DiscountValue = sc(Text)`
  // eslint-disable-next-line local-rules/css-layout-animation
  transition: font-size var(--animation-duration);
  font-size: ${FONT_SIZES.px40};

  ${desktopAndUp(css`
    font-size: ${FONT_SIZES.px56};
  `)}

  [data-collapsed='true'] & {
    font-size: ${FONT_SIZES.px24};
  }
`;

const DiscountCondition = sc(Text)`
  // eslint-disable-next-line local-rules/css-layout-animation
  transition: font-size var(--animation-duration);

  [data-collapsed='true'] & {
    font-size: ${FONT_SIZES.px10};
  }
`;

const Discount = sc.div`
  display: flex;
  flex-direction: column;
  margin-right: 36px;
  font-size: ${FONT_SIZES.px56};
  font-weight: ${FONT_WEIGHTS.heavy};
  color: inherit;
  text-align: right;
  // eslint-disable-next-line local-rules/css-layout-animation
  transition: margin var(--animation-duration);
  transform-origin: left;

  [data-collapsed='true'] & {
    margin-right: 16px;
  }
`;

const HeadlineContainer = sc.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const Category = sc(Text)`
  text-transform: uppercase;
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: ${FONT_SIZES.px20};
  color: inherit;
  transition: opacity var(--animation-duration);

  [data-collapsed='true'] & {
    opacity: 0;
  }
`;

const Headline = sc(Text)<{$preventVerticalAdjustment?: boolean}>`
  font-size: ${FONT_SIZES.px24};
  font-weight: ${FONT_WEIGHTS.heavy};
  line-height: ${FONT_SIZES.px28};
  color: inherit;
  // eslint-disable-next-line local-rules/css-layout-animation
  transition: transform var(--animation-duration),
    font-size var(--animation-duration);
  transform-origin: left;

  [data-collapsed='true'] & {
    ${({$preventVerticalAdjustment}) =>
      !$preventVerticalAdjustment &&
      css`
        transform: translateY(-14px);
      `}
    line-height: 1.42;
    font-size: ${FONT_SIZES.px14};
  }
`;

const StyledCouponTiers = sc(CouponTiers)<{$hasBrandLogo: boolean}>`
  display: flex;
  margin: 0 auto;
  height: 90px;
  transform-origin: left;

  ${tabletAndDown(
    css`
      height: 66px;
      font-size: ${FONT_SIZES.px20};
    `,
  )}

  ${({$hasBrandLogo}) =>
    $hasBrandLogo &&
    css`
      margin: 0;

      @media ${mediaMaxS} {
        height: 55px;
        font-size: ${FONT_SIZES.px24};
      }
    `}

  [data-collapsed='true'] & {
    margin: unset;
    height: 45px;
    font-size: ${FONT_SIZES.px20};
  }
`;

const StyledBrandLogo = sc(BrandLogo)`
  width: 148px;
  padding: 16px 24px;
  border-radius: 8px;
  background-color: ${COLORS.White};

  transition: transform var(--animation-duration),
    opacity var(--animation-duration);

  ${tabletAndDown(
    css`
      width: 90px;
      padding: 12px;
    `,
  )}

  [data-collapsed='true'] & {
    transform: translateX(-130px);
    opacity: 0;
    width: 0;
    padding: 0;
  }
`;

const ContentAndBrandLogo = sc.div`
  display: flex;
  align-items: center;
  flex: 1 0 100%;
  justify-content: space-between;
  gap: 20px;

  [data-collapsed='true'] & {
    gap: unset;
  }
`;
